import React from 'react'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { Theme, useTheme } from '@mui/material/styles'
import {
  BarPlot,
  BarSeriesType,
  ChartContainer,
  ChartsReferenceLine,
  ChartsXAxis,
} from '@mui/x-charts'
import { useContainerDimensions } from '../../../../hooks/useContainerDimensions'

import { BarChartProps } from '../../../../types/Charts/BarChart'

import { Legend } from './ChartLegend'
import { CustomBarPlot } from './CustomBarPlot'

export const BarChart = ({
  amounts,
  color,
  data,
  isMini = false,
  heightOverride,
  margin = {
    bottom: 30,
    left: 40,
    right: 40,
    top: 20,
  },
  legendLabelAvg,
  legendLabelMain,
  selectedTabIndex,
  removeSelectedTabColorChange,
  onBarClick,
}: BarChartProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const { height, width } = useContainerDimensions(containerRef)
  const theme = useTheme()

  const lowestValue = data.reduce((min, item) => Math.min(min, item.value), Infinity)
  const isNegative = lowestValue < 0

  const colorRange = React.useMemo(() => {
    const defaultColor = `${color}80`
    if (removeSelectedTabColorChange) {
      // If we don't want to change the color of the selected tab
      return data.map(() => color)
    }

    if (selectedTabIndex === undefined) {
      return [...new Array(data.length - 1).fill(defaultColor), color]
    }
    return data.map((_, index) => (index === selectedTabIndex ? color : defaultColor))
  }, [color, data, selectedTabIndex])

  const handleBarClick = React.useCallback(
    (_: React.MouseEvent<SVGElement, MouseEvent>, { dataIndex }: { dataIndex: number }) =>
      onBarClick && onBarClick(dataIndex),
    [onBarClick],
  )

  const chartData = React.useMemo(() => {
    const values = data.map(({ value }) => value)
    const minValue = Math.min(...values)
    const maxValue = Math.max(...values)

    return {
      series: [
        {
          data: values,
          type: 'bar',
        },
      ] as BarSeriesType[],
      xAxis: [
        {
          data: data.map(({ label }) => label),
          scaleType: 'band' as const,
          categoryGapRatio: 0.45,
        },
      ],
      yAxis: [
        {
          scaleType: 'linear' as const,
          min: minValue < 0 ? minValue * 1.2 : 0, // Extend the Y-axis range below the lowest negative value
          max: maxValue > 0 ? maxValue : 0, // Ensure the Y-axis includes positive values
        },
      ],
    }
  }, [data])

  const standardHeight = isMini ? 120 : 172

  return (
    <Stack alignItems="center" sx={{ mb: isMini ? 16 : 24, width: '100%' }}>
      <Box
        ref={containerRef}
        sx={{
          height: heightOverride ? heightOverride : standardHeight,
          mb: 8,
          touchAction: 'pan-y',
          width: '100%',
          '& svg': {
            touchAction: 'pan-y',
            userSelect: 'none',
          },
          '& .recharts-wrapper': {
            touchAction: 'pan-y',
          },
        }}
      >
        <ChartContainer
          height={height}
          margin={{
            top: margin.top || 45,
            right: margin.right,
            bottom: margin.bottom,
            left: margin.left,
          }}
          series={chartData.series}
          width={width}
          xAxis={chartData.xAxis}
          yAxis={chartData.yAxis.map((axis) => ({ ...axis, width: 0 }))}
        >
          {amounts?.average && ( // show average line if available
            <ChartsReferenceLine
              labelAlign="middle"
              lineStyle={{
                stroke: (theme as Theme).palette.neutral.darker,
                strokeDasharray: '0.1 5.9',
                strokeLinecap: 'round',
                strokeWidth: 2,
              }}
              y={amounts?.average}
            />
          )}

          {isNegative && ( // show lowest value line if negative values are allowed and if the lowest value is negative
            <ChartsReferenceLine
              labelAlign="middle"
              lineStyle={{
                stroke: (theme as Theme).palette.neutral.dark,
              }}
              y={0}
            />
          )}

          <ChartsXAxis
            disableTicks={true}
            slots={{
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              axisTickLabel: ({ text, ownerState: _, ...restProps }) => {
                const label = data[selectedTabIndex ?? data.length - 1]?.label
                return (
                  <text
                    {...restProps}
                    style={
                      {
                        ...restProps.style,
                        fontWeight: !removeSelectedTabColorChange && label === text ? 'bold' : 400,
                      } as React.CSSProperties
                    }
                  >
                    {text}
                  </text>
                )
              },
            }}
            sx={(theme) => ({
              '.MuiChartsAxis-line': {
                stroke: !isNegative ? (theme as Theme).palette.neutral?.main : 'none', // If there is a negative value hide this line and use the reference line
              },
              'MuiChartsAxis-root-MuiChartsXAxis-root': {
                transform: 'translate(0, 150)',
              },
              '.MuiChartsAxis-tickLabel': {
                transform: isNegative ? 'translateY(15px)' : {}, // Add padding to move labels further down. Must use an empty transform to prevent errors
              },
            })}
            tickLabelStyle={{
              fontSize: 11,
            }}
          />
          <BarPlot
            onItemClick={handleBarClick}
            slots={{
              bar: (props) => {
                const { ownerState, ...defaultProps } = props
                return (
                  <CustomBarPlot
                    {...defaultProps}
                    colorRange={colorRange}
                    data={data}
                    dataIndex={ownerState.dataIndex}
                    removeSelectedTabColorChange={removeSelectedTabColorChange}
                    selectedTabIndex={selectedTabIndex ?? data.length - 1}
                    style={{
                      height: props.height,
                      x: props.x,
                      y: props.y,
                      width: props.width,
                    }}
                  />
                )
              },
            }}
          />
        </ChartContainer>
      </Box>

      {(legendLabelMain || legendLabelAvg) && (
        <Legend
          average={amounts?.formattedAverage}
          color={color}
          legendLabelAvg={legendLabelAvg}
          legendLabelMain={legendLabelMain}
          legendStyle={{ mr: margin.left! + margin.right! }}
        />
      )}
    </Stack>
  )
}
